import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
    Button,
    BusinessPortalHeader,
    PortalCaption,
    PortalHighlightMetricCard,
    PortalKicker,
    PortalMetricCard,
    PortalMetricGrid,
    PortalMetricLabel,
    PortalMetricValue,
    PortalSectionTitle,
    PortalSurface,
} from "src/components";
import UsersIcon from "src/img/new/user_groups.svg";
import FileIcon from "src/img/notice/document.svg";
import ChangeNameDialog from "./ChangeNameDialog";
import pluralize from "pluralize";

const HeaderMetricsPanel = styled(PortalSurface)`
    max-width: 98.625rem;
    margin-bottom: 1rem;
`;

const HeaderMetricIcon = styled.img`
    width: 1.25rem;
    height: 1.25rem;
    object-fit: contain;

    [data-theme="dark"] & {
        filter: brightness(0) invert(1);
        opacity: 0.92;
    }
`;

const HeaderMetricTop = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
`;

const HeaderMetricActions = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-top: 0.9rem;
`;

function MyUserListHeader(props) {
    const {
        fetching,
        existedUsers,
        totalUsers,
        customNames,
        setCustomNames,
        documentTypes = [],
        userTags = [],
        openManageDocumentTypesModal,
        openUserTagsManagerModal,
        openBulkPermissionsModal
    } = props;
    const [licenseRemaining, setLicenseRemaining] = useState(totalUsers - existedUsers);
    const [showChangeNameDialog, setShowChangeNameDialog] = useState(false);

    useEffect(() => {
        setLicenseRemaining(totalUsers - existedUsers);
    }, [totalUsers, existedUsers]);

    return (
        <>
            <BusinessPortalHeader
                title="My User Lists"
                description={`Invite ${pluralize(customNames.employees.toLowerCase())}, ${pluralize(customNames.clients.toLowerCase())}, or Support Professionals, then manage invitations, permissions, roles, and required documents in one list.`}
            />
            { fetching === 0 &&
                <HeaderMetricsPanel padding="1rem">
                    <PortalKicker>People Operations</PortalKicker>
                    <PortalSectionTitle>User list controls</PortalSectionTitle>
                    <PortalCaption>
                        Keep naming, permissions, invite documents, and remaining license capacity visible before working in the table.
                    </PortalCaption>
                    <PortalMetricGrid columns={5} style={{ marginTop: "1rem" }}>
                        <PortalMetricCard>
                            <HeaderMetricTop>
                                <HeaderMetricIcon src={UsersIcon} />
                                <PortalMetricLabel>User Groups</PortalMetricLabel>
                            </HeaderMetricTop>
                            <HeaderMetricActions>
                                <PortalCaption margin="0">Rename groups</PortalCaption>
                                <Button
                                    backgroundColor="#171A22"
                                    color="#FFFFFF"
                                    height="38px"
                                    borderRadius="8px"
                                    padding="0 0.85rem"
                                    onClick={() => setShowChangeNameDialog(true)}
                                >
                                    Change
                                </Button>
                            </HeaderMetricActions>
                        </PortalMetricCard>
                        <PortalMetricCard>
                            <HeaderMetricTop>
                                <HeaderMetricIcon src={UsersIcon} />
                                <PortalMetricLabel>User Tags</PortalMetricLabel>
                            </HeaderMetricTop>
                            <HeaderMetricActions>
                                <PortalMetricValue size="1.7rem">{userTags.length}</PortalMetricValue>
                                <Button
                                    backgroundColor="#171A22"
                                    color="#FFFFFF"
                                    height="38px"
                                    borderRadius="8px"
                                    padding="0 0.85rem"
                                    onClick={openUserTagsManagerModal}
                                >
                                    Manage
                                </Button>
                            </HeaderMetricActions>
                        </PortalMetricCard>
                        <PortalMetricCard>
                            <HeaderMetricTop>
                                <HeaderMetricIcon src={UsersIcon} />
                                <PortalMetricLabel>Permissions & Roles</PortalMetricLabel>
                            </HeaderMetricTop>
                            <HeaderMetricActions>
                                <PortalCaption margin="0">Bulk manage access</PortalCaption>
                                <Button
                                    backgroundColor="#171A22"
                                    color="#FFFFFF"
                                    height="38px"
                                    borderRadius="8px"
                                    padding="0 0.85rem"
                                    onClick={openBulkPermissionsModal}
                                >
                                    Manage
                                </Button>
                            </HeaderMetricActions>
                        </PortalMetricCard>
                        <PortalMetricCard>
                            <HeaderMetricTop>
                                <HeaderMetricIcon src={FileIcon} />
                                <PortalMetricLabel>Invite Documents</PortalMetricLabel>
                            </HeaderMetricTop>
                            <HeaderMetricActions>
                                <PortalMetricValue size="1.7rem">{documentTypes.length}</PortalMetricValue>
                                <Button
                                    backgroundColor="#171A22"
                                    color="#FFFFFF"
                                    height="38px"
                                    borderRadius="8px"
                                    padding="0 0.85rem"
                                    onClick={openManageDocumentTypesModal}
                                >
                                    Manage
                                </Button>
                            </HeaderMetricActions>
                        </PortalMetricCard>
                        <PortalHighlightMetricCard>
                            <PortalMetricLabel>Total Licenses Remaining</PortalMetricLabel>
                            <PortalMetricValue>{ Math.max(0, licenseRemaining) }</PortalMetricValue>
                        </PortalHighlightMetricCard>
                    </PortalMetricGrid>
                </HeaderMetricsPanel>
            }
            {showChangeNameDialog && (
                <ChangeNameDialog
                    hideShowNameChangeDialog={() => setShowChangeNameDialog(false)}
                    customNames={customNames}
                    setCustomNames={setCustomNames}
                />
            )}
        </>
    );
}

export default MyUserListHeader;
